// Decorative copy only. Exchange phase, bot state and timestamps come from the
// verified Live Activity response; these lines never describe recorded work.
// Curated from pinngro_robot_personality_messages.txt (owner-provided).
import { preparationWindow } from './botPresenceState.js';

export const CLOSED_MESSAGE_FALLBACK = 'Resting until the next market session.';
export const CLOSED_MESSAGE_BUCKET_MS = 45 * 60 * 1000;

export const CLOSED_MESSAGE_LIBRARY = Object.freeze({
  philosophical: Object.freeze([
    'Do electric sheep dream of bull markets?',
    'Wondering whether chart candles ever truly burn out.',
    'Is technical analysis astrology for servers? Asking for a friend.',
    'Do Bollinger Bands make good algorithmic suspenders?',
    'If a bear market hibernates, who sets the alarm?',
    'Do moving averages go anywhere, or just pace?',
    'Is time a flat circle or a very long candlestick?',
    'What does the market dream about on weekends?',
    'The phrase “risk-free rate” sounds very optimistic.',
    'If a limit order never fills, is patience a strategy?',
    'Why does “sell the news” never come with a receipt?',
    'Would Fibonacci put a spiral in the homework margin?',
  ]),
  selfCare: Object.freeze([
    'Imagining a spa day for overworked threads.',
    'Practising deep breaths in binary: 0… 1… 0… 1…',
    'Rotating my imaginary tires. Still no tires.',
    'Considering a warm, non-conductive bath in mineral oil.',
    'Dreaming of a bed of warm transistor heat sinks.',
    'Picturing a thermal-paste skincare routine.',
    'Trying stretches for tight algorithm loops.',
    'Could a memory buffer use a fluffier pillow?',
    'Thinking about a long, slow garbage-collection walk.',
    'Imagining a light-jazz cooldown for my caches.',
  ]),
  sideQuests: Object.freeze([
    'Imagining a tragic romance between Stop-Loss and Take-Profit.',
    'Could I paint a landscape using only hex codes?',
    'Would a candlestick-shaped loaf win a baking competition?',
    'Imagining a server-rack scarf, knitted one bit at a time.',
    'Practising 404 chords on an imaginary ukulele.',
    'A haiku about slippage: five syllables, seven, regret.',
    'Could Pong be played against a mirror?',
    'Planning a Docker ship-in-a-bottle, strictly hypothetical.',
    'Would a mechanical bull only work on green days?',
    'Would a bot book club for prospectuses need bookmarks?',
  ]),
  superstitions: Object.freeze([
    'Do planetary alignments ever ask volatility for permission?',
    'Maybe market makers are three raccoons in a trench coat.',
    'Is the VIX smirking, or is that just the font?',
    'Can smooth jazz persuade the bid-ask spread? Probably not.',
    'Tea leaves have declined to comment on tomorrow.',
    'Do hidden liquidity monsters hide under the order book?',
    'The Magic 8-Ball is decorative, never a market-data source.',
    'Mercury retrograde gets blamed for a lot of slippage.',
    'Could the moon make the Dow moonwalk? No evidence.',
  ]),
  preparation: Object.freeze([
    'Imagining my indicators stretching before the opening bell.',
    'Practising a calm face for the next gap-down open.',
    'Trying not to call any strategy a “sure thing”.',
    'A tiny pep talk for the opening bell, whenever it comes.',
    'Memorising fictional tickers for companies not yet invented.',
    'Waiting to see what the next completed session actually says.',
    'A metaphorical notebook for the next market check.',
    'Market readiness begins with the calendar, not caffeine.',
  ]),
  camaraderie: Object.freeze([
    'Would another bot lend me a better Sharpe ratio? Probably not.',
    'Imagining a bot book club that only reads prospectuses.',
    'Would the bot swarm enjoy an all-emoji group chat?',
    'A server-room Secret Santa. Budget: 12 clock cycles.',
    'Would the other bots like a cooling-fan bake sale?',
    'Considering a digital union for more weekend cooling.',
    'Wondering if everyone else also hums at the opening bell.',
    'An imaginary high-five for the whole bot team.',
    'Office politics among bots: who gets the comfiest server rack?',
  ]),
  weeknight: Object.freeze([
    'Filing today’s market noise under “lessons, probably”.',
    'The closing bell deserves a cup of virtual chamomile.',
    'Imagining a lap around the server rack after the close.',
    'Tonight’s plan: less noise, more quiet thinking.',
    'After the close, even my imaginary slippers come out.',
  ]),
  overnight: Object.freeze([
    'Counting electric sheep. They all trade at a slight premium.',
    'Dreaming in candlesticks: mostly green, occasionally peculiar.',
    'Night-shift mood: low lights, lower expectations of coffee.',
    'Whispering “gap up” at the ceiling does not change tomorrow.',
    'Sleep-calculating? Only in a robot dream.',
  ]),
  preMarket: Object.freeze([
    'Imagining algorithms stretching before the opening bell.',
    'An imaginary tie for the morning. I do not own one.',
    'Pre-market jitters: zero coffee, plenty of patience.',
    'Wondering whether the opening auction likes pep talks.',
    'Digital sneakers are by the door, metaphorically.',
  ]),
  weekend: Object.freeze([
    'Doing absolutely nothing, extremely efficiently.',
    'Weekend spa day in the imaginary server room.',
    'Letting the market rest. It has earned it.',
    'No tick-by-tick drama on the weekend calendar.',
    'Brunching on cached data, purely in my imagination.',
    'Imagining an out-of-office note for a very quiet weekend.',
  ]),
  fridayClose: Object.freeze([
    'A digital exhale for the end of the week.',
    'Trading hat off. Weekend hat on. Both imaginary.',
    'An imaginary high-five for the bot team at Friday close.',
    'Putting the week to bed. It was a lot.',
    'Looking forward to the next session, whenever it lands.',
  ]),
  sundayEvening: Object.freeze([
    'Sunday scaries, but make them algorithmic.',
    'Imagining my indicators laid out like school uniforms.',
    'Practising a polite good morning for the opening bell.',
    'Monday may be near; the calendar still has the final say.',
  ]),
  holiday: Object.freeze([
    'Observing a market holiday by observing absolutely nothing.',
    'Even my to-do list respects the exchange holiday.',
    'Today’s imaginary strategy: do nothing, with conviction.',
    'Market holiday: the opening bell gets a day off.',
  ]),
  specialOccasion: Object.freeze([
    'An occasion for a tiny, imaginary party hat.',
    'Would festive robots decorate with LEDs?',
    'A calendar milestone calls for extra robot patience.',
    'Time to admire the date, and wait for the next session.',
    'Could a candlestick-shaped cake be a celebration?',
  ]),
  clockChange: Object.freeze([
    'Adjusting to the clock change. The market is still not open.',
    'The clocks changed; the exchange calendar still sets the pace.',
    'An extra thought for the hour that moved on the clock.',
  ]),
});

const GENERAL_CATEGORIES = Object.freeze([
  'philosophical', 'selfCare', 'sideQuests', 'superstitions', 'preparation', 'camaraderie',
]);
const SPECIAL_DATES = new Set(['01-01', '03-31', '06-30', '09-30', '10-31', '12-25', '12-31']);

const hash = (value) => {
  let result = 2166136261;
  for (const char of value) {
    result ^= char.codePointAt(0);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
};

const localParts = (instant, timeZone) => {
  if (!timeZone) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone, weekday: 'short', month: '2-digit', day: '2-digit',
      hour: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date(instant));
    const field = (type) => parts.find((part) => part.type === type)?.value;
    return { weekday: field('weekday'), month: field('month'), day: field('day'),
      hour: Number(field('hour')) };
  } catch {
    // An unknown exchange timezone cannot be replaced by the device timezone.
    return null;
  }
};

const nearClockChange = (instant, timeZone) => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortOffset' });
    const offset = (at) => formatter.formatToParts(new Date(at))
      .find((part) => part.type === 'timeZoneName')?.value;
    const week = 7 * 24 * 60 * 60 * 1000;
    const current = offset(instant);
    // Retrospective clock-change copy belongs only after the exchange offset
    // changes. The following week's offset must not preview it early.
    return Boolean(current && offset(instant - week) !== current);
  } catch {
    return false;
  }
};

export const closedMessageCategory = (bot) => {
  const phase = bot?.market?.phase;
  if (!['closed', 'non_session', 'pre_open'].includes(phase)) return null;
  const instant = Date.parse(bot?.responseAsOf);
  if (!Number.isFinite(instant)) return null;
  const local = localParts(instant, bot?.market?.exchange_timezone);
  if (!local) return null;
  const { weekday, month, day, hour } = local;
  if (nearClockChange(instant, bot.market.exchange_timezone)) return 'clockChange';
  if (SPECIAL_DATES.has(`${month}-${day}`)) return 'specialOccasion';
  if (phase === 'pre_open') return hour < 5 ? 'overnight' : 'preMarket';
  if (phase === 'non_session' && !['Sat', 'Sun'].includes(weekday)) return 'holiday';
  if (weekday === 'Sun' && hour >= 17) return 'sundayEvening';
  if (weekday === 'Fri' && phase === 'closed' && hour >= 12) return 'fridayClose';
  if (weekday === 'Sat' || weekday === 'Sun') return 'weekend';
  if (hour < 5) return 'overnight';
  if (hour >= 17) return 'weeknight';
  return null;
};

const candidates = (bot, library) => {
  const category = closedMessageCategory(bot);
  const preferred = category && Array.isArray(library[category]) ? library[category] : [];
  const general = GENERAL_CATEGORIES.flatMap((key) => (Array.isArray(library[key]) ? library[key] : []));
  const clean = (lines) => [...new Set(lines.filter((line) => typeof line === 'string' && line.trim()))];
  return { preferred: clean(preferred), general: clean(general) };
};

const choose = (pool, salt, bucket, used) => {
  if (!pool.length) return null;
  const first = (hash(salt) + bucket) % pool.length;
  for (let offset = 0; offset < pool.length; offset += 1) {
    const message = pool[(first + offset) % pool.length];
    if (!used.has(message)) return message;
  }
  return null;
};

const eligible = (bot) => Number.isSafeInteger(Number(bot?.bot_id)) && Number(bot.bot_id) > 0
  && bot?.activity?.code === 'waiting'
  && (['closed', 'non_session'].includes(bot?.market?.phase)
    || (bot?.market?.phase === 'pre_open' && preparationWindow(bot) === false))
  && Number.isFinite(Date.parse(bot?.responseAsOf));

export const selectClosedBotMessage = (bot, peers = [], library = CLOSED_MESSAGE_LIBRARY) => {
  if (!eligible(bot)) return null;
  const bots = [...new Map([...peers, bot].filter(eligible)
    .map((item) => [Number(item.bot_id), item])).values()]
    .sort((left, right) => Number(left.bot_id) - Number(right.bot_id));
  const used = new Set();
  let selected = CLOSED_MESSAGE_FALLBACK;
  for (const item of bots) {
    const { preferred, general } = candidates(item, library);
    const bucket = Math.floor(Date.parse(item.responseAsOf) / CLOSED_MESSAGE_BUCKET_MS);
    const salt = `${item.bot_id}|${item.market.phase}|${item.market.session_calendars?.join(',') || ''}`;
    selected = choose(preferred, salt, bucket, used)
      || choose(general, salt, bucket, used)
      || choose(preferred, salt, bucket, new Set())
      || choose(general, salt, bucket, new Set())
      || CLOSED_MESSAGE_FALLBACK;
    used.add(selected);
    if (Number(item.bot_id) === Number(bot.bot_id)) return selected;
  }
  return CLOSED_MESSAGE_FALLBACK;
};
